import { Component } from '@theme/component';
import { fetchCartState, companionOverageUpdates } from '@theme/cart-state';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /**
   * True while a /cart/add.js request is in flight. Nothing else gates a second
   * submit: the only pre-existing check is "is any add-to-cart button already
   * disabled", and no code path disables the button for the duration of the add
   * (`disable()` fires only on the quantity-max error and from #onVariantUpdate).
   * So a mobile double-tap posted two adds — and in companion mode that is TWO
   * purifiers AND two filter subscriptions, since both lines merge by variant +
   * identical properties. sticky-add-to-cart.js has no guard of its own and
   * re-fires `.click()` on every tap, which made it worse.
   *
   * Deliberately a flag rather than `disable()`/`enable()` on the buttons:
   * `enable()` would re-enable a button that #onVariantUpdate had disabled for
   * an unavailable variant.
   * @type {boolean}
   */
  #addInFlight = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @param {{fresh?: boolean}} [options] - `fresh: true` is required after
   *   this form's own /cart/add.js resolves: the shared fetch would
   *   otherwise join an in-flight request started before the add and apply
   *   pre-add state (see @theme/cart-state).
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity({ fresh = false } = {}) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      // Shared, deduplicated fetch: after an add, this fires alongside
      // cart-upsell's refresh and every other product-form instance reacting
      // to the same cart event — all can join one request.
      const cart = await fetchCartState({ fresh });

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Per-variant cart cap check for selector-less PDPs
   * (settings.cart_max_per_variant via data-max-per-variant). Counts what the
   * cart already holds from the RENDERED drawer rows — zero network, and the
   * drawer is server-rendered with current state on every load. Sums ACROSS
   * lines of the variant (an `_upsell` line and a plain line are separate
   * cart lines but one variant). Same result shape as the quantity
   * selector's canAddToCart() so the existing error UI handles both.
   * @param {HTMLFormElement} form
   * @returns {{canAdd: boolean, maxQuantity: number} | null} null when no cap applies
   */
  #capValidationFromCart(form) {
    const max = Number(this.dataset.maxPerVariant) || 0;
    if (max <= 0) return null;
    const variantId = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'))?.value;
    if (!variantId) return null;
    let inCart = 0;
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      document.querySelectorAll(`cart-items-component tr[data-key^="${variantId}:"] input[data-cart-line]`)
    );
    for (const input of inputs) inCart += Number(input.value) || 0;
    return { canAdd: inCart + this.#getQuantity() <= max, maxQuantity: max };
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    // Drop repeat submits while an add is already on the wire (see #addInFlight).
    if (this.#addInFlight) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId) {
      // Selector-based validation when the PDP renders a quantity selector;
      // otherwise the per-variant cart cap still applies via the rendered
      // drawer rows (#capValidationFromCart) — many PDPs on this store ship
      // no quantity block, so the cap must not depend on one.
      const validation = this.refs.quantitySelector?.canAddToCart
        ? this.refs.quantitySelector.canAddToCart()
        : this.#capValidationFromCart(form);

      if (validation && !validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const canBuildAttributionProperties =
      window.Attribution && typeof window.Attribution.buildLineItemProperties === 'function';

    if (canBuildAttributionProperties) {
      const properties = window.Attribution.buildLineItemProperties(form, overrideVariantId);
      if (properties && Object.keys(properties).length > 0) {
        for (const [key, value] of Object.entries(properties)) {
          formData.set(`properties[${key}]`, value);
        }
      }
    }

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    // Companion filter subscription (channel-selling-plan-selector companion
    // mode): the form carries filter_variant_id + filter_selling_plan hidden
    // inputs. Strip them from the FormData — they must never reach the
    // single-item endpoint — and switch to a two-line-item JSON add:
    // the unit one-time and the filter with its selling plan.
    const filterVariantId = formData.get('filter_variant_id');
    const filterSellingPlan = formData.get('filter_selling_plan');
    formData.delete('filter_variant_id');
    formData.delete('filter_selling_plan');

    if (filterVariantId && filterSellingPlan) {
      const unitVariantId = String(formData.get('id') ?? '');
      const unitQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

      /** @type {Record<string, string>} */
      const unitProperties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && match[1]) unitProperties[match[1]] = String(value);
      }

      /** @type {Record<string, string>} */
      let filterProperties = {};
      if (canBuildAttributionProperties) {
        filterProperties = { ...window.Attribution.buildLineItemProperties(form, String(filterVariantId)) };
        // Channel context (_iom_*) applies to both lines, but product-scoped
        // values belong to the filter, not the unit.
        delete filterProperties['_one_time_purchase_price'];
      }
      try {
        const companionScript = this.querySelector('script[data-channel-companion-data]');
        const companion = companionScript?.textContent ? JSON.parse(companionScript.textContent) : null;
        if (companion?.productId) filterProperties['_product_id'] = String(companion.productId);
        if (companion?.variantSku) filterProperties['_variant_sku'] = String(companion.variantSku);
      } catch {
        /* companion data is display/attribution sugar — never block the add */
      }
      // Pairs the subscription line with its unit so the cart renders it as
      // an attached checkbox card and removing the unit removes it too.
      filterProperties['_filter_for_variant'] = unitVariantId;

      // Order matters: lines are added sequentially (newest first in
      // cart.items) and the cart renders oldest-first, so [unit, filter]
      // displays the unit ABOVE its subscription card — the order the cart
      // pairing relies on.
      this.#processBatchAddToCart(
        [
          { variantId: unitVariantId, quantity: unitQuantity, properties: unitProperties },
          {
            variantId: String(filterVariantId),
            // Quantity MIRRORS the unit: each unit gets its own filter
            // subscription. The cart steppers keep the two lines in sync
            // after the add (component-cart-items.js quantity mirror).
            quantity: unitQuantity,
            sellingPlan: Number(filterSellingPlan),
            properties: filterProperties,
          },
        ],
        event
      );
      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    // Append once — appending inside the loop sent duplicate `sections`
    // params, making Shopify render the drawer section multiple times.
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    this.#addInFlight = true;

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Start the post-add cart refresh BEFORE dispatching: fresh
          // replaces any stale in-flight /cart.js as the shared promise, so
          // event listeners below (cart-upsell, other product forms) that
          // fetch via @theme/cart-state join post-add data. Not awaited —
          // the dispatch must not wait on it.
          this.#fetchAndUpdateCartQuantity({ fresh: true });

          // Dispatch immediately so the drawer opens and the section morph
          // starts now — the drawer HTML is already in response.sections.
          // Awaiting the /cart.js refresh here added a full serial round
          // trip to every add. Listeners that need the cart payload
          // (cart-upsell, other product forms) fetch it themselves when
          // resource is undefined.
          this.dispatchEvent(
            new CartAddEvent(undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          if (window.Attribution && typeof window.Attribution.syncCartAttributes === 'function') {
            window.Attribution.syncCartAttributes().catch(() => {});
          }
        }
      })
      .catch((error) => {
        console.error(error);

        // Same reason as the batch path: without an error dispatch the button
        // stays "Added!" over an empty cart and sticky-add-to-cart.js's
        // `data-puppet` flag never clears.
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
          })
        );
      })
      .finally(() => {
        this.#addInFlight = false;

        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Adds multiple items in one JSON /cart/add.js call. When an item carries
   * explicit `properties` they are used verbatim; otherwise attribution
   * properties are built from the form (the variant-change race path).
   * `sellingPlan` turns that line into a subscription.
   * @param {Array<{variantId: string, quantity: number, sellingPlan?: number, properties?: Record<string, string>}>} items
   * @param {Event} [event]
   */
  #processBatchAddToCart(items, event) {
    if (items.length === 0) return;
    // Drop repeat submits while an add is already on the wire (see #addInFlight).
    // Reached with the flag still false on the companion path: #processAddToCart
    // delegates here and returns before arming its own single-item fetch.
    if (this.#addInFlight) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const form = this.querySelector('form');
    const payload = {
      items: items.map((item) => {
        /** @type {{id: number, quantity: number, selling_plan?: number, properties?: Record<string, string>}} */
        const baseItem = {
          id: Number(item.variantId),
          quantity: item.quantity,
        };

        if (item.sellingPlan) {
          baseItem.selling_plan = item.sellingPlan;
        }

        let properties = item.properties;

        if (!properties) {
          const canBuildAttributionProperties =
            form &&
            window.Attribution &&
            typeof window.Attribution.buildLineItemProperties === 'function';
          if (canBuildAttributionProperties) {
            properties = window.Attribution.buildLineItemProperties(form, item.variantId);
          }
        }

        if (properties && Object.keys(properties).length > 0) {
          baseItem.properties = properties;
        }

        return baseItem;
      }),
      sections: cartItemComponentsSectionIds.join(','),
    };

    this.#addInFlight = true;

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          // A capped companion add is NOT all-or-nothing: Shopify keeps the
          // unit at its cap and the subscription at the requested quantity
          // (add 4 at a cap of 3 → unit 3, subscription 4). Correct it here,
          // before the error dispatch below, so the drawer's re-render on that
          // event already shows the clamped cart.
          if (items.some((item) => item.sellingPlan)) {
            await this.#clampCompanionsAfterCappedAdd(cartItemComponentsSectionIds);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        // Fresh refresh started before dispatch, same as #processAddToCart:
        // listeners joining the shared /cart.js get post-add data.
        this.#fetchAndUpdateCartQuantity({ fresh: true });

        // Dispatch immediately (see #processAddToCart) — don't hold the
        // drawer open/morph hostage to the /cart.js refresh.
        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );

        if (window.Attribution && typeof window.Attribution.syncCartAttributes === 'function') {
          window.Attribution.syncCartAttributes().catch(() => {});
        }
      })
      .catch((error) => {
        console.error(error);

        // A network/parse failure here used to dispatch NOTHING, which left the
        // button stuck in its "Added!" state over an empty cart and — because
        // sticky-add-to-cart.js only clears `data-puppet` on cartUpdate/cartError
        // — permanently suppressed add-to-cart feedback on the main button for
        // the rest of the session. Mirror the single-item path's error dispatch.
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
            productId: this.dataset.productId,
          })
        );
      })
      .finally(() => {
        this.#addInFlight = false;

        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Clamps companion subscription lines back down to their unit's quantity
   * after a capped add (see companionOverageUpdates), then dispatches the
   * corrected cart so the drawer morphs and the badge takes the absolute
   * count. Keys come from a fresh /cart.js read, never the DOM. Never throws:
   * a failure here leaves the cart as Shopify left it, no worse than before.
   * @param {string[]} sectionIds - Cart sections to render in the response.
   * @returns {Promise<void>}
   */
  async #clampCompanionsAfterCappedAdd(sectionIds) {
    try {
      const cart = await fetchCartState({ fresh: true });
      const updates = Array.isArray(cart?.items) ? companionOverageUpdates(cart.items) : {};
      if (Object.keys(updates).length === 0) return;

      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', { body: JSON.stringify({ updates, sections: sectionIds.join(',') }) })
      );
      const updated = await response.json();
      if (updated.status || !Array.isArray(updated.items)) {
        console.error('[cart] companion clamp failed', updated.description || updated.message);
        return;
      }

      // Not 'product-form-component': cart-icon.js treats that source as an
      // additive count, and this is the absolute cart.
      this.dispatchEvent(
        new CartUpdateEvent(updated, 'companion-clamp', {
          source: 'companion-clamp',
          itemCount: updated.item_count,
          sections: updated.sections,
        })
      );
    } catch (error) {
      console.error('[cart] could not clamp companion quantities after a capped add', error);
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];

      // Re-enter the SINGLE-item path, not #processBatchAddToCart. The companion
      // filter-subscription branch (filter_variant_id + filter_selling_plan → a
      // two-line add) lives in #processAddToCart, and the queue only ever carried
      // {variantId, quantity} — so flushing through the batch path added the unit
      // ALONE. Shopper taps a swatch and then Add to Cart inside the variant-change
      // window (~100-600ms), sees the success animation and the drawer open, and
      // gets the purifier with no subscription. #processAddToCart re-reads the form,
      // which the selector has since re-rendered for the new variant.
      //
      // Repeat taps inside one window collapse to a single add — the same intent as
      // the #addInFlight guard.
      const lastQueued = queuedItems[queuedItems.length - 1];
      if (lastQueued) {
        this.#processAddToCart(lastQueued.variantId, lastQueued.quantity);
      }
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
